import React, { useEffect, useRef } from 'react';
import { useState } from 'react';
import { FiMenu } from 'react-icons/fi';
import { ReactComponent as Logo } from '../Assets/ASightLogo.svg';
import { Icon } from '../Components/UI/Icon';
import ProfileAvatar from '../Components/Icons/ProfileAvatar';
import BellButton from '../Components/Icons/BellButton';
import NotificationsPanel from '../Components/Notifications/NotificationsPanel';
import useNotifications from '../Hooks/useNotifications';
import { useNavigate } from 'react-router-dom';
import ProfiloModal from '../Components/Modal/ProfiloModal';
import useGet from '../Hooks/useGet';
import API_ENDPOINTS from '../Config/api';
import itFlag from "../Assets/it.svg";
import gbFlag from "../Assets/gb.svg";
import LanguageSelector from '../Components/LanguageSelector';
import { useRecoilValue } from 'recoil';
import { langAtom, changeLanguage } from '../Atom/langAtom';
import { useTranslation } from 'react-i18next';
import { fetchWithAuth } from '../Utils/fetchWithAuth';
import { PlayCircle, ChevronDown, ClipboardList } from 'lucide-react';

// Icona profilo SVG stile classico (replicata qui per uso nel menu)
function ClassicProfileIcon({ className }) {
  return (
    <svg viewBox="0 0 128 128" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="64" cy="64" r="64" fill="#e5e8ec" />
      <circle cx="64" cy="54" r="32" fill="#545d6a" />
      <ellipse cx="64" cy="108" rx="44" ry="28" fill="#545d6a" />
    </svg>
  );
}


export default function Navbar({ onMenuClick }) {
  // Modal state for Profilo
  const [showAziendaModal, setShowAziendaModal] = useState(false);
  const [showProfiloModal, setShowProfiloModal] = useState(false);
  const [aziendaForm, setAziendaForm] = useState({
    nome_azienda: '',
    indirizzo: '',
    email: '',
    telefono: ''
  });
  const [aziendaLoading, setAziendaLoading] = useState(false);
  const [aziendaError, setAziendaError] = useState('');
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [notifOpen, setNotifOpen] = React.useState(false);
  const [tourIndexOpen, setTourIndexOpen] = React.useState(false);
  const menuRef = useRef(null);
  const { unreadCount } = useNotifications({pollInterval: 10000});
  const language = useRecoilValue(langAtom);
  const { t } = useTranslation();
  const { fetchedData: aziendaDettagli, refetch: refetchAzienda } = useGet(API_ENDPOINTS.aziendaDettagli);
    // Update form when aziendaDettagli changes
    useEffect(() => {
      if (aziendaDettagli) {
        setAziendaForm({
          nome_azienda: aziendaDettagli.nome_azienda || '',
          indirizzo: aziendaDettagli.indirizzo || '',
          email: aziendaDettagli.email || '',
          telefono: aziendaDettagli.telefono || ''
        });
      }
    }, [aziendaDettagli]);
    // Handle form changes
    const handleAziendaFormChange = (e) => {
      const { name, value } = e.target;
      setAziendaForm((prev) => ({ ...prev, [name]: value }));
    };

    // Handle form submit
    const handleAziendaFormSubmit = async (e) => {
      e.preventDefault();
      setAziendaLoading(true);
      setAziendaError('');
      try {
        const response = await fetch('/api/aziende/me/', {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionStorage.getItem('access_token')}`
          },
          body: JSON.stringify(aziendaForm)
        });
        if (!response.ok) throw new Error('Errore salvataggio');
        await refetchAzienda();
        setShowAziendaModal(false);
      } catch (err) {
        setAziendaError('Errore nel salvataggio.');
      } finally {
        setAziendaLoading(false);
      }
    };
  const { fetchedData: brandingLogo, loading: brandingLoading } = useGet(API_ENDPOINTS.brandingLogo);

  useEffect(() => {
    function handleClickOutside(event) {
      if (document.querySelector('[data-guided-tour]')) return;
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
        setTourIndexOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [tourMenuActive, setTourMenuActive] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      const open = !!e.detail?.open;
      setMenuOpen(open);
      setTourMenuActive(open);
    };
    window.addEventListener('tour-profile-menu', handler);
    return () => window.removeEventListener('tour-profile-menu', handler);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('lang', language);
    localStorage.setItem('language', language);
  }, [language]);

  const toggleLanguage = () => {
    const newLang = language === 'it' ? 'en' : 'it';
    changeLanguage(newLang);
  };

  const handleChangePassword = () => {
    navigate('/change-password');
  };

  const handleLogout = () => {
    const refChannel = sessionStorage.getItem('ref_channel');
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('ref_channel');
    sessionStorage.removeItem('token');
    window.location.href = refChannel ? `/?ref=${encodeURIComponent(refChannel)}` : '/';
  };

  const handleStartTourSection = async (route) => {
    setMenuOpen(false);
    setTourIndexOpen(false);
    try {
      await fetchWithAuth(API_ENDPOINTS.onboardingReset, { method: 'POST' });
    } catch (e) { /* ignore */ }
    navigate(route || '/dashboard');
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('tour-reset', { detail: { startRoute: route } }));
    }, 400);
  };

  return (
  <header className="h-16 bg-gradient-to-r from-white lg:pl-[70px] via-yellow-100 to-yellow-300 border-b shadow-md flex items-center justify-between px-6 lg:px-10 relative z-20">
      <div className="flex items-center">
        <button
          onClick={onMenuClick}
          className="lg:hidden text-gray-600 text-2xl mr-4"
          aria-label="Apri menu laterale"
        >
          <FiMenu />
        </button>

        {/* Non mostrare logo durante loading per evitare flickering */}
        {!brandingLoading && (brandingLogo?.logo_url ? (
          <img src={brandingLogo.logo_url} alt="Logo" draggable="false" className="pl-5 h-12 max-w-[144px] object-contain" style={{ userSelect: 'none' }} />
        ) : (
          <Logo className="pl-5 py-5 w-36" aria-label="Logo" />
        ))}
      </div>

      <div ref={menuRef} className="relative">
        <div className="flex items-center space-x-2">
          <div className="relative" data-tour="navbar-notifications">
            <BellButton count={unreadCount} onClick={() => setNotifOpen(v => !v)} />
            {notifOpen && <NotificationsPanel isOpen={notifOpen} onClose={() => setNotifOpen(false)} />}
          </div>

          <button
            onClick={() => setMenuOpen(o => !o)}
            className="flex items-center space-x-2 text-gray-700"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            data-tour="navbar-profile"
          >
          {/* Nome solo su desktop */}
          <span className="font-medium hidden lg:block">
            {aziendaDettagli?.nome_azienda ?? ''}
          </span>

          {/* Solo omino su mobile */}
          <span className="w-8 h-8 inline-flex items-center justify-center">
            <ProfileAvatar className="w-8 h-8" />
          </span>
          </button>
        </div>

        {menuOpen && (
          <div
            role="menu"
            className={`absolute right-0 mt-2 bg-white border shadow rounded py-1 w-56 text-sm ${tourMenuActive ? 'z-[10001]' : 'z-50'}`}
          >
            {/* Nome azienda */}
            <div className="px-4 py-2 font-semibold text-gray-800">
              {aziendaDettagli?.nome_azienda ?? '—'}
            </div>

            <div className="border-t my-1" />

            {/* Visualizza Tour */}
            <div>
              <button
                onClick={() => setTourIndexOpen(v => !v)}
                role="menuitem"
                className="w-full flex items-center justify-between px-4 py-2 hover:bg-gray-100"
              >
                <span className="flex items-center">
                  <PlayCircle size={16} className="mr-2 text-yellow-500" />
                  {t('tour.restart_tour', 'Visualizza Tour')}
                </span>
                <ChevronDown size={14} className={`text-gray-400 transition-transform duration-200 ${tourIndexOpen ? 'rotate-180' : ''}`} />
              </button>
              {tourIndexOpen && (
                <div className="bg-gray-50 py-1 max-h-52 overflow-y-auto border-t border-b border-gray-100">
                  <button
                    onClick={() => handleStartTourSection(null)}
                    className="w-full text-left px-6 py-1.5 text-sm font-semibold text-yellow-600 hover:bg-yellow-50"
                  >
                    {t('tour.tour_full', 'Tour Completo')}
                  </button>
                  {[
                    { route: '/dashboard', label: 'Dashboard' },
                    { route: '/pluginadmin', label: 'Plugin Admin' },
                    { route: '/compliance', label: 'Compliance' },
                    { route: '/myassets', label: 'My Assets' },
                    { route: '/cyberlog', label: 'Cyber Log' },
                    { route: '/cybersurveillance', label: 'Cyber Surveillance' },
                    { route: '/networking', label: 'Networking' },
                    { route: '/awareness', label: 'Awareness' },
                    { route: '/enterpriseConsent', label: 'Enterprise' },
                  ].map((section) => (
                    <button
                      key={section.route}
                      onClick={() => handleStartTourSection(section.route)}
                      className="w-full text-left px-6 py-1.5 text-sm text-gray-600 hover:bg-yellow-50 hover:text-yellow-700"
                    >
                      {section.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Ordini */}
            <button
              onClick={() => { setMenuOpen(false); navigate('/ordini'); }}
              role="menuitem"
              className="w-full flex items-center px-4 py-2 hover:bg-gray-100"
            >
              <ClipboardList size={16} className="mr-2 text-gray-500" />
              Ordini
            </button>

            {/* Profilo */}
            <button
              onClick={() => { setMenuOpen(false); setShowProfiloModal(true); }}
              role="menuitem"
              className="w-full flex items-center px-4 py-2 hover:bg-gray-100"
            >
                <span className="w-5 h-5 inline-flex items-center justify-center mr-2 align-middle">
                <ProfileAvatar className="w-5 h-5" />
              </span>
              Profilo
            </button>

            {/* Cambia Password */}
            <button
              onClick={handleChangePassword}
              role="menuitem"
              className="w-full flex items-center px-4 py-2 hover:bg-gray-100"
            >
              <Icon name="key" className="mr-2 text-base" />
              {t('changePassword')}
            </button>

            {/* Sicurezza MFA */}
            {(() => {
              const isSso = !!sessionStorage.getItem('sso_impersonation');
              return (
                <button
                  onClick={isSso ? undefined : () => { setMenuOpen(false); navigate('/mfa-setup'); }}
                  role="menuitem"
                  disabled={isSso}
                  data-tour="navbar-mfa-security"
                  className={`w-full flex items-center px-4 py-2 ${
                    isSso ? 'text-gray-400 cursor-not-allowed opacity-50' : 'hover:bg-gray-100'
                  }`}
                >
                  <Icon name="starshield" className="mr-2 text-base" />
                  {t('mfa.menuLabel', 'Sicurezza MFA')}
                </button>
              );
            })()}

            {/* Language selector (replaces toggle) */}
            <div className="w-full px-4 py-2 hover:bg-gray-100">
              <LanguageSelector />
            </div>

            <div className="border-t my-1" />

            {/* Logout */}
            <button
              onClick={handleLogout}
              role="menuitem"
              className="w-full flex items-center px-4 py-2 hover:bg-gray-100 text-red-500"
            >
              <Icon name="logout" className="mr-2 text-base" />
              Logout
            </button>
          </div>
        )}

        {/* Modal Profilo globale */}
        <ProfiloModal isOpen={showProfiloModal} onClose={() => setShowProfiloModal(false)} />
      </div>
    </header>
  );
}
